import { useState, useEffect } from 'react';
import { useSelectExtractAssetsStore } from '../../../services/selectExtractAssetsStore';
import { config } from '../../../config';
import { prepareHeaders } from '../../../configuration/tokenHandling/prepareHeaders';
import { IGoodPractices } from '../models/GoodPractices';
import { ITrip } from '../models/Trip';
import { IEvent } from '../models/Event';
import TableViewToggles, { type TableViewTogglesViewType } from '@rio-cloud/rio-uikit/TableViewToggles';
import { useDateFilterStore } from '../../../services/dateFilterStore';

type UseGoodPracticesDataProps = {
  externalViewType: TableViewTogglesViewType;
  hasData: boolean;
};

export const useGoodPracticesData = ({
  externalViewType,
  hasData,
}: UseGoodPracticesDataProps) => {
  const { selectedExtractAssets } = useSelectExtractAssetsStore();
  const { startDate, endDate } = useDateFilterStore();
  const [goodPracticesList, setGoodPracticesList] = useState<any>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [searchValue, setSearchValue] = useState('');
  const [viewType, setViewType] = useState(externalViewType || TableViewToggles.VIEW_TYPE_TABLE);
  const [expandedRows, setExpandedRows] = useState<string[]>([]);
  let bonusesMap = new Map<string, string>();
  bonusesMap.set('IT_OVERNIGHT', 'Pernoite com Tanque Cheio')
  bonusesMap.set('IT_TRIP_HOURS', 'Duração da Viagem')
  bonusesMap.set('IT_DAY_HOURS', 'Jornada Diária')

  const handleViewTypeChange = (newViewType: TableViewTogglesViewType) => setViewType(newViewType);

  const handleToggleRow = (id: string) => {
    setExpandedRows(prev => (prev.includes(id) ? prev.filter(expanded => expanded !== id) : [...prev, id]));
  };

  useEffect(() => {
    if (!hasData) {
      setIsLoading(true);
      setTimeout(() => setIsLoading(false), 1000);
      setGoodPracticesList([]);
      return;
    }

    const getAssets = async () => {
      setIsLoading(true);
      setError(null);
      try {
        const headers = await prepareHeaders(new Headers());
        const response = await fetch(config.backend.SCORES_SERVICE + '/v2/scores/good-practices', {
          method: 'POST',
          headers,
          body: JSON.stringify({ assetId: selectedExtractAssets[0], start_date:startDate, end_date: endDate }),
        });
        if (!response.ok) {
            throw new Error(`HTTP error! status: ${response.status}`);
        }
        const jsonData = (await response.json()) as IGoodPractices;
        const trips = jsonData.items;

        const tripsList = trips.map((item: ITrip) => ({
          tripStart: item.tripStart,
          tripFinal: item.tripFinal,
          itOvernight: item.bonuses.IT_OVERNIGHT,
          itDayHours: item.bonuses.IT_DAY_HOURS,
          itTripHours: item.bonuses.IT_TRIP_HOURS,
          items: item.items.map((event: IEvent) => ({
            detailLastUpdate: event.refUtc,
            detailEventCoordinates: {
              lng: event.longitude,
              lat: event.latitude,
            },
            detailEvents: bonusesMap.get(event.type),
          })),
        }));

        setGoodPracticesList(tripsList);
      } catch (e: any) {
        console.error('Error fetching good practices:', e);
        setError(e.message);
      } finally {
        setIsLoading(false);
      }
    };

    if(hasData){
      getAssets();
    }
  }, [selectedExtractAssets, startDate, endDate ]);

  return {
    goodPracticesList,
    isLoading,
    error,
    searchValue,
    setSearchValue,
    handleViewTypeChange,
    viewType,
    handleToggleRow,
    expandedRows,
  };
};